/**
 * Brain files + tmux data contract — web mirror of
 * `Sources/Models/BrainFileModels.swift`.
 *
 * Mirrors the Brain Worker's OWNER-ONLY REST endpoints (`claude-brain-mcp`
 * docs/BRAIN_API.md, `src/files-tmux-api.ts`). Unlike `/memories`, this surface is
 * Mac-control, owner-only, deny-by-default: it dispatches to the operator's Mac
 * bridge, so it REQUIRES the engine (bridge daemon) online and can take up to ~25s
 * (or time out at 504). Authed with the caller's long-lived `brain_*` bearer token
 * — never the short-lived Supabase JWT.
 *
 * Endpoints:
 *   GET /files?path=<dir>  -> { path, count, entries: FileEntry[] }   (default path ~)
 *   GET /tmux              -> { count, sessions: TmuxSession[] }
 */

/** One entry in a directory listing. Mirrors `GET /files`. */
export interface FileEntry {
  name: string;
  /** "file" | "dir". */
  type: string;
  size: number | null;
  mtime: string | null;
  /** Absolute path on the operator's Mac (used to navigate into a directory). */
  path: string | null;
}

export function isDirectory(e: FileEntry): boolean {
  return e.type.toLowerCase() === 'dir';
}

/** `{ path, count, entries }` — a directory listing. */
export interface FilesListResponse {
  path: string;
  count: number;
  entries: FileEntry[];
}

/** One live tmux session on the operator's Mac. Mirrors `GET /tmux`. */
export interface TmuxSession {
  name: string;
  createdAt: string | null;
  windows: number | null;
  attached: boolean | null;
  paneCurrentCommand: string | null;
  paneCurrentPath: string | null;
  panePid: number | null;
}

/** `{ count, sessions }` — the live tmux sessions. */
export interface TmuxListResponse {
  count: number;
  sessions: TmuxSession[];
}

// MARK: - snake_case → camelCase decoders (the API's wire shape)

interface FileEntryWire {
  name: string;
  type: string;
  size?: number | null;
  mtime?: string | null;
  path?: string | null;
}

interface FilesListWire {
  path: string;
  count: number;
  entries: FileEntryWire[];
}

interface TmuxSessionWire {
  name: string;
  created_at?: string | null;
  windows?: number | null;
  attached?: boolean | null;
  pane_current_command?: string | null;
  pane_current_path?: string | null;
  pane_pid?: number | null;
}

interface TmuxListWire {
  count: number;
  sessions: TmuxSessionWire[];
}

export function decodeFileEntry(w: FileEntryWire): FileEntry {
  return {
    name: w.name,
    type: w.type,
    size: w.size ?? null,
    mtime: w.mtime ?? null,
    path: w.path ?? null,
  };
}

export function decodeFilesList(w: FilesListWire): FilesListResponse {
  return {
    path: w.path,
    count: w.count,
    entries: (w.entries ?? []).map(decodeFileEntry),
  };
}

export function decodeTmuxSession(w: TmuxSessionWire): TmuxSession {
  return {
    name: w.name,
    createdAt: w.created_at ?? null,
    windows: w.windows ?? null,
    attached: w.attached ?? null,
    paneCurrentCommand: w.pane_current_command ?? null,
    paneCurrentPath: w.pane_current_path ?? null,
    panePid: w.pane_pid ?? null,
  };
}

export function decodeTmuxList(w: TmuxListWire): TmuxListResponse {
  return {
    count: w.count,
    sessions: (w.sessions ?? []).map(decodeTmuxSession),
  };
}

// MARK: - Errors + owner-only / engine-offline classification

/** Shared error for the owner-only files/tmux surface. Carries the HTTP status
 * so views can render the right owner-only vs engine-offline copy. */
export class BrainFsError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = 'BrainFsError';
  }
}

/** How the UI should present a files/tmux failure. Maps the documented status
 * codes (401 / 403 / 502 / 503 / 504) plus transport failures. */
export type BrainFsErrorKind = 'notSignedIn' | 'ownerOnly' | 'engineOffline' | 'other';

/** Classify a files/tmux error for owner-only vs engine-offline UI copy. */
export function brainFsKind(error: unknown): BrainFsErrorKind {
  if (error instanceof BrainFsError && error.status != null) {
    switch (error.status) {
      case 401:
        return 'notSignedIn';
      case 403:
        return 'ownerOnly';
      case 502:
      case 503:
      case 504:
        return 'engineOffline';
      default:
        return 'other';
    }
  }
  // A raw transport failure (fetch threw, no HTTP response) means we never
  // reached the engine — treat as offline.
  if (error instanceof TypeError) return 'engineOffline';
  return 'other';
}
